import { ref } from "vue";
import { defineStore } from "pinia";
import type { IOfferV1 } from "@smeetz/offers-common";
import request from "@/utils/request";

interface GroupOffersApiResponse {
  status: string;
  data?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    offerList?: any[];
  };
}

/**
 * Holds the active group's offers list. Populated once at app startup (or on
 * first need) and persisted to localStorage via `pinia.helper.ts` so we don't
 * re-fetch on page reload.
 *
 * The `getGroupOffers` callback registered with `@smeetz/offers-common`
 * delegates here, and components read `offers` directly instead of calling the
 * package's `getOrFetchGroupOffers` themselves.
 */
export const useGroupOffersStore = defineStore("groupOffers", () => {
  const offers = ref<IOfferV1[]>([]);

  function setOffers(newOffers: IOfferV1[]): void {
    offers.value = [...newOffers];
  }

  function clearOffers(): void {
    offers.value = [];
  }

  /**
   * Fetches offers for the given group from the backend and stores them.
   * Returns the fetched offers. Use {@link getOffers} to read from cache
   * first; this method always hits the API.
   */
  async function fetchOffers(groupId: number): Promise<IOfferV1[]> {
    const response = await request({
      url: `/group/${groupId}/offer`,
      method: "get",
    });
    const payload = response.data as GroupOffersApiResponse;
    const fetched = (payload.data?.offerList ?? []).map((offer) => ({
      ...offer,
      state: offer.state === 1,
      groupId: offer.group.id,
      membership: offer.membershipToScan ?? undefined,
    })) as IOfferV1[];

    setOffers(fetched);
    return fetched;
  }

  /**
   * Returns the cached offers if any, otherwise fetches and caches them.
   * Entry point used by `discount.service.ts`'s `initGroupOffersClient`
   * callback and by components that need offers.
   */
  async function getOffers(groupId: number): Promise<IOfferV1[]> {
    if (offers.value.length > 0) return offers.value;
    return fetchOffers(groupId);
  }

  return {
    offers,
    setOffers,
    clearOffers,
    fetchOffers,
    getOffers,
  };
});
